import React from "react";
import { Alert, Descriptions, Table, Tag } from "antd";
import {
  OTHER_SURFACE_PREVIEW_STATUS,
  OTHER_SURFACE_PREVIEW_STATUS_META,
} from "constants/tradeCapture";
import { formatToNDecimalPlaces } from "helpers/generalHelper";

const EMPTY = "—";

const isBlank = (value) => value === null || value === undefined || value === "";

// Same precision as the Other surface table: premium 3 dp, vol 2 dp as %.
const formatPremium = (value) =>
  isBlank(value) ? EMPTY : formatToNDecimalPlaces(3, value);

// The preview sends vol as a decimal (0.71); the surface shows 71.00%.
const formatVol = (value) =>
  isBlank(value) ? EMPTY : `${formatToNDecimalPlaces(2, Number(value) * 100)}%`;

const formatUnderlying = (value) => (isBlank(value) ? EMPTY : value);

const COLUMNS = [
  { title: "Strike", dataIndex: "strike", key: "strike", width: 100 },
  { title: "C/P", dataIndex: "cp", key: "cp", width: 70 },
  {
    title: "Current Premium",
    dataIndex: "current_premium",
    key: "current_premium",
    width: 130,
    render: formatPremium,
  },
  {
    title: "New Premium",
    dataIndex: "new_premium",
    key: "new_premium",
    width: 130,
    render: formatPremium,
  },
  {
    title: "New Vol",
    dataIndex: "new_vol",
    key: "new_vol",
    width: 100,
    render: formatVol,
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: 110,
    render: (status) => {
      const meta = OTHER_SURFACE_PREVIEW_STATUS_META[status];
      return <Tag color={meta?.color}>{meta?.label || status}</Tag>;
    },
  },
];

/** Current vs new for the tenor an Other surface fill would replace. */
const OtherSurfacePreview = ({ preview }) => {
  const rows = Array.isArray(preview?.rows) ? preview.rows : [];
  const countOf = (status) => rows.filter((r) => r.status === status).length;
  const removedCount = countOf(OTHER_SURFACE_PREVIEW_STATUS.REMOVED);
  const noVolCount = countOf(OTHER_SURFACE_PREVIEW_STATUS.NO_VOL);

  return (
    <div className="trade-capture-preview">
      <Descriptions
        size="small"
        column={{ xs: 1, sm: 3 }}
        className="trade-capture-preview__summary"
      >
        <Descriptions.Item label="Tenor">
          <strong>{preview?.tenor || EMPTY}</strong>
        </Descriptions.Item>
        <Descriptions.Item label="Current UL">
          {formatUnderlying(preview?.current_underlying)}
        </Descriptions.Item>
        <Descriptions.Item label="New UL">
          <strong>{formatUnderlying(preview?.new_underlying)}</strong>
        </Descriptions.Item>
      </Descriptions>

      {(removedCount > 0 || noVolCount > 0) && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={[
            removedCount > 0 &&
              `${removedCount} strike(s) on the surface are not in the screenshot and will be removed.`,
            noVolCount > 0 &&
              `${noVolCount} strike(s) have no vol for their premium; their premium will be left blank.`,
          ]
            .filter(Boolean)
            .join(" ")}
        />
      )}

      <div className="trade-capture-table">
        <Table
          dataSource={rows}
          columns={COLUMNS}
          rowKey={(row) => `${row.strike}-${row.status}`}
          rowClassName={(row) =>
            `trade-capture-preview__row--${row.status || "unknown"}`
          }
          pagination={false}
          scroll={{ x: "max-content", y: 480 }}
          size="small"
          bordered
        />
      </div>
    </div>
  );
};

export default OtherSurfacePreview;
